<template lang="pug">
    .newsletter
        .body(v-loading="loading")
            .tab-content.scroll-box(ref="scrollContainer" @scroll="handleScroll" v-dragscroll)
                .empty(v-if="!loading && !hasTemplates")
                    .icon
                        i.el-icon-fa-envelope.fas
                    h4 {{ $t('No Templates') }}
                    p {{ $t('No newsletter templates available for this POS.') }}
                    p.hint {{ $t('Create a newsletter template with "POS Template" enabled.') }}
                template(v-else)
                    .empty(v-if="!sentNewslettersLoading && (!sentNewsletters || !sentNewsletters.length)")
                        .icon
                            i.el-icon-fa-slash.fas
                            i.el-icon-fa-circle.far
                        h4 {{ $t('Empty List') }}
                        p {{ $t('There is nothing to see here!') }}
                    section.row.sent-newsletters(v-else)
                        //- .label
                        //-     i.el-icon-fa-paper-plane.fas
                        //-     | {{ $t('Newsletters') }}
                        .panel
                            .sent-list
                                .sent-item(
                                    v-for="newsletter in sentNewsletters"
                                    :key="newsletter.id"
                                    :class="{ 'is-draft': newsletter.status === 'draft', 'is-sending': newsletter.status === 'sending', 'is-clickable': newsletter.status === 'draft' }"
                                    @click="onNewsletterClick(newsletter)"
                                )
                                    .status-badge.test(v-if="newsletter.status === 'draft'")
                                        | {{ $t('Test') }}
                                    .status-badge.sending(v-else-if="newsletter.status === 'sending'")
                                        i.el-icon-loading
                                        | {{ $t('Sending') }}
                                    .item-content
                                        .info
                                            .subject {{ newsletter.subject }}
                                            .meta
                                                span.date {{ formatDate(newsletter.started_sending_at || newsletter.created_at) }}
                                                span.recipients(v-if="newsletter.recipients && newsletter.status !== 'draft'")
                                                    i.el-icon-fa-users.fas
                                                    | {{ newsletter.recipients }}
                                                template(v-if="newsletter.status === 'sent'")
                                                    span.received
                                                        i.el-icon-fa-envelope.fas
                                                        | {{ newsletter.received || 0 }}
                                                    span.opens
                                                        i.el-icon-fa-eye.fas
                                                        | {{ newsletter.opens || 0 }}
                                                    span.clicks
                                                        i.el-icon-fa-mouse-pointer.fas
                                                        | {{ newsletter.clicks || 0 }}
                                                    span.skipped(v-if="newsletter.skipped")
                                                        i.el-icon-fa-forward.fas
                                                        | {{ newsletter.skipped }}
                                                    span.bounces(v-if="newsletter.bounces")
                                                        i.el-icon-fa-exclamation-triangle.fas
                                                        | {{ newsletter.bounces }}
                                        .actions
                                            el-button.preview-btn(
                                                v-if="newsletter.status === 'sent'"
                                                type="text"
                                                icon="el-icon-fa-eye far"
                                                @click.stop="openPreview(newsletter)"
                                            )
                                    el-progress.sending-progress(
                                        v-if="newsletter.status === 'sending'"
                                        :percentage="getProgress(newsletter)"
                                        :stroke-width="6"
                                        :show-text="true"
                                    )
                                .load-more-spinner(v-if="sentNewslettersLoading && sentNewsletters.length")
                                    i.el-icon-loading
        .foot
            .actions
                el-button.action(
                    type="primary"
                    icon="el-icon-fa-plus fas"
                    :disabled="!hasTemplates || sending"
                    @click="openCreateDrawer"
                ) {{ $t('Create Newsletter') }}
                .placeholder.gray-box.grow

        create-newsletter-drawer(
            ref="createNewsletterDrawer"
            @sent="onNewsletterSent"
            @focus-search="$emit('focus-search')"
            @blur-search="$emit('blur-search')"
        )

        el-drawer.newsletter-preview-drawer(
            :title="$t('Newsletter Preview')"
            :visible.sync="previewDrawerVisible"
            :modal="true"
            size="640px"
            @close="onPreviewClose"
        )
            .preview-content(v-if="previewDrawerVisible")
                .loading-state(v-if="previewLoading" v-loading="true")
                .empty-state(v-else-if="!previewHtml")
                    i.el-icon-fa-envelope-open.far
                    p {{ $t('No preview available') }}
                iframe.preview-iframe(
                    v-else
                    :srcdoc="previewHtml"
                    sandbox="allow-same-origin"
                )
</template>

<i18n>
{
    "de": {
        "No Templates": "Keine Vorlagen",
        "No newsletter templates available for this POS.": "Keine Newsletter-Vorlagen für diese Kasse verfügbar.",
        "Create a newsletter template with \"POS Template\" enabled.": "Erstellen Sie eine Newsletter-Vorlage mit aktivierter \"POS-Vorlage\".",
        "Newsletters": "Newsletter",
        "Test": "Test",
        "Sending": "Wird gesendet",
        "Empty List": "Leere Liste",
        "There is nothing to see here!": "Hier gibt es nichts zu sehen!",
        "Create Newsletter": "Newsletter erstellen",
        "Newsletter Preview": "Newsletter-Vorschau",
        "No preview available": "Keine Vorschau verfügbar",
        "Failed to load preview": "Vorschau konnte nicht geladen werden"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useSetupStore, usePosSetupStore, usePosNewsletterStore } from '@/stores';
import CreateNewsletterDrawer from './Partials/CreateNewsletterDrawer';
import { timestamp2date } from '@/filters';
import echo from '@/echo';

export default {
    name: 'PosNewsletter',
    components: {
        CreateNewsletterDrawer,
    },
    data() {
        return {
            echoChannel: null,
            previewDrawerVisible: false,
            previewLoading: false,
            previewHtml: null
        };
    },
    computed: {
        setupStore() {
            return useSetupStore();
        },
        posSetupStore() {
            return usePosSetupStore();
        },
        posNewsletterStore() {
            return usePosNewsletterStore();
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        activePointOfSale() {
            return this.posSetupStore.activePointOfSale;
        },
        loading() {
            return this.posNewsletterStore.templates.loading;
        },
        hasTemplates() {
            return this.posNewsletterStore.hasTemplates;
        },
        sentNewsletters() {
            return this.posNewsletterStore.sentNewsletters.items;
        },
        sentNewslettersLoading() {
            return this.posNewsletterStore.sentNewsletters.loading;
        },
        sentNewslettersPageInfo() {
            return this.posNewsletterStore.sentNewsletters.pageInfo;
        },
        hasMorePages() {
            const pageInfo = this.sentNewslettersPageInfo;
            return pageInfo && pageInfo.lastPage > pageInfo.currentPage;
        },
        sending() {
            return this.posNewsletterStore.sending;
        }
    },
    watch: {
        activePointOfSale: {
            handler(pos) {
                if (pos && pos.id) {
                    this.fetchData();
                }
            },
            immediate: true
        },
        activeCms: {
            handler(cms) {
                if (cms && cms.id) {
                    this.initializeWebSocketListener();
                }
            },
            immediate: true
        }
    },
    methods: {
        initializeWebSocketListener() {
            if (!echo.client) {
                setTimeout(() => this.initializeWebSocketListener(), 100);
                return;
            }

            // Clean up previous channel if exists
            if (this.echoChannel) {
                this.echoChannel.stopListening('.newsletter:statistics-updated');
            }

            const cmsId = _.get(this.activeCms, 'id');
            if (!cmsId) return;

            this.echoChannel = echo.client.private('cms.' + cmsId)
                .listen('.newsletter:statistics-updated', data => {
                    this.updateNewsletterStats(data);
                });
        },
        updateNewsletterStats(data) {
            // Update through the store for proper reactivity
            this.posNewsletterStore.updateNewsletterStats(data.id, data);
        },
        fetchData() {
            const posId = this.activePointOfSale && this.activePointOfSale.id;
            if (posId) {
                this.posNewsletterStore.fetchTemplates();
                this.posNewsletterStore.fetchSentNewsletters(posId).then(() => {
                    this.$nextTick(() => {
                        this.loadMoreIfNeeded(this.$refs.scrollContainer);
                    });
                });
            }
        },
        handleScroll(event) {
            this.loadMoreIfNeeded(event.target);
        },
        loadMoreIfNeeded(target) {
            if (!target) return;

            const currentPage = this.sentNewslettersPageInfo.currentPage;
            const lastPage = this.sentNewslettersPageInfo.lastPage;

            if (this.sentNewslettersLoading || lastPage <= currentPage) {
                return;
            }

            let loadMore = false;
            const offset = window.innerHeight;
            if (Math.ceil(target.scrollTop) >= target.scrollHeight - target.offsetHeight - offset) {
                loadMore = true;
            }

            if (loadMore) {
                const posId = this.activePointOfSale && this.activePointOfSale.id;
                if (posId) {
                    this.posNewsletterStore.fetchSentNewsletters(posId, currentPage + 1, 10, true).then(() => {
                        this.$nextTick(() => {
                            this.loadMoreIfNeeded(this.$refs.scrollContainer);
                        });
                    });
                }
            }
        },
        formatDate(timestamp) {
            if (!timestamp) return '';
            return timestamp2date(timestamp, 'dd.MM.yyyy, HH:mm');
        },
        getProgress(newsletter) {
            // Check direct progress first (set by WebSocket), then statusLog
            if (newsletter.progress != null) {
                return newsletter.progress;
            }
            if (newsletter.statusLog && newsletter.statusLog.progress != null) {
                return newsletter.statusLog.progress;
            }
            return 0;
        },
        openCreateDrawer() {
            if (this.$refs.createNewsletterDrawer) {
                this.$refs.createNewsletterDrawer.open();
            }
        },
        onNewsletterSent() {
            this.$notify.success({
                title: this.$t('Newsletter sent'),
                iconClass: 'fas el-icon-fa-check',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 5000
            });
        },
        onNewsletterClick(newsletter) {
            // Only allow clicking on drafts to continue editing
            if (newsletter.status === 'draft' && this.$refs.createNewsletterDrawer) {
                // Pass the draft to open() so it can set the ID after reset()
                this.$refs.createNewsletterDrawer.openWithDraft(newsletter);
            }
        },
        async openPreview(newsletter) {
            this.previewDrawerVisible = true;
            this.previewLoading = true;
            this.previewHtml = null;

            try {
                const html = await this.posNewsletterStore.fetchNewsletterPreview(newsletter.id);
                this.previewHtml = html;
            } catch (error) {
                console.error('Error loading newsletter preview:', error);
                this.$notify.error({
                    title: this.$t('Error'),
                    message: this.$t('Failed to load preview'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 5000
                });
            } finally {
                this.previewLoading = false;
            }
        },
        onPreviewClose() {
            this.previewHtml = null;
        }
    },
    mounted() {
        this.$emit('focus-search');
    },
    beforeDestroy() {
        // Clean up WebSocket listener
        if (this.echoChannel) {
            this.echoChannel.stopListening('.newsletter:statistics-updated');
        }
    }
};
</script>

<style lang="sass" scoped>
.newsletter
    display: contents

.tab-content
    width: 100%
    display: flex
    flex-direction: column

.body
    min-height: 10rem
    position: relative
    flex-grow: 1
    .scroll-box
        padding: 1rem
        position: absolute
        left: 0
        top: 0
        width: 100%
        height: 100%
        overflow-x: hidden
        overflow-y: auto

.empty
    flex-grow: 1
    display: flex
    flex-direction: column
    justify-content: center
    align-items: center
    padding: 4rem
    text-align: center
    .icon
        position: relative
        font-size: 4rem
        color: darken($color-skeleton, 20%)
        i.el-icon-fa-slash
            position: absolute
            left: -0.15rem
            top: 0.35rem
            transform: rotate(100deg)
    h4
        margin-top: 1rem
        margin-bottom: 0
        font-size: 2rem
        font-weight: 300
        color: darken($color-skeleton, 30%)
    p
        color: rgba(0,0,0,0.4)
        &.hint
            font-size: 0.9em
            font-style: italic

section.row
    .label
        padding: 6px 12px
        margin-bottom: 0.5rem
        i
            margin-right: 0.5rem
            color: $color-grey-text
    .panel
        background: rgba(255,255,255, 0.4)
        border-radius: 0.5rem
        padding: 0.5rem

.sent-list
    display: flex
    flex-direction: column
    gap: 0.5rem

.load-more-spinner
    display: flex
    justify-content: center
    padding: 1rem
    color: $color-grey-text
    i
        font-size: 1.5rem

.sent-item
    display: flex
    flex-direction: column
    padding: 0.75rem 1rem
    background: white
    border-radius: 4px
    box-shadow: 0 1px 3px rgba(0,0,0,0.1)
    position: relative
    &.is-draft
        background: rgba($--color-warning, 0.05)
        border: 1px dashed rgba($--color-warning, 0.4)
    &.is-sending
        background: rgba($--color-info, 0.05)
        border: 1px solid rgba($--color-info, 0.3)
    &.is-clickable
        cursor: pointer
        &:hover
            background: rgba($--color-warning, 0.1)
    .status-badge
        position: absolute
        top: 0.5rem
        right: 0.5rem
        color: white
        font-size: 0.7em
        font-weight: 600
        text-transform: uppercase
        padding: 2px 6px
        border-radius: 3px
        display: flex
        align-items: center
        gap: 4px
        &.test
            background: $--color-warning
        &.sending
            background: $--color-info
    .item-content
        display: flex
        align-items: center
        justify-content: space-between
        width: 100%
    .info
        flex-grow: 1
        .subject
            font-weight: 500
            margin-bottom: 0.25rem
        .meta
            font-size: 0.85em
            color: $color-grey-text
            display: flex
            gap: 1rem
            align-items: center
            flex-wrap: wrap
            span i
                margin-right: 0.25rem
            span.received
                color: $--color-success
            span.opens
                color: #409EFF
            span.clicks
                color: $--shop-color-primary
            span.skipped
                color: $--color-warning
            span.bounces
                color: $--color-danger
    .actions
        display: flex
        align-items: center
        margin-left: 0.5rem
        .preview-btn
            padding: 8px
    &::v-deep .el-progress
        display: flex
        align-items: center
        flex-direction: row-reverse
        width: 100%
        margin-top: 0.75rem
        .el-progress-bar
            padding-right: 0
            margin-right: 0
        .el-progress__text
            margin-left: 0
            margin-right: 10px

.foot
    padding: 1rem
    display: grid
    gap: 0.5rem
    grid-template-rows: 50px
    grid-template-columns: repeat(12, minmax(0, 1fr))
    .gray-box
        height: 50px
        background: darken($color-skeleton, 10%)
        padding: 4px 1rem
        padding-bottom: 6px
        border-radius: 4px
    .actions
        grid-column: 1 / -1
        display: flex
        gap: 0.5rem
        .grow
            flex-grow: 1
        .action
            font-size: 1.2rem

.newsletter-preview-drawer
    &::v-deep .el-drawer__header
        margin-bottom: 0
        padding: 20px
        border-bottom: 1px solid rgba(0,0,0,0.1)
    &::v-deep .el-drawer__body
        display: flex
        flex-direction: column
        overflow: hidden
        padding: 0

.preview-content
    flex: 1
    display: flex
    flex-direction: column
    overflow: hidden

.loading-state
    flex: 1
    min-height: 200px

.empty-state
    flex: 1
    display: flex
    flex-direction: column
    justify-content: center
    align-items: center
    color: $color-grey-text
    i
        font-size: 3rem
        margin-bottom: 1rem
    p
        font-size: 1.1rem

.preview-iframe
    flex: 1
    width: 100%
    border: none
    background: white
</style>
